/**
 * Reporting period range + interval.
 *
 * Both dates are optional and mean "no bound on that side" when empty, so the
 * default is "every period the documents cover" rather than an arbitrary window
 * the user has to guess at.
 */

import React from "react";
import {
	INTERVAL_LABELS,
	PERIOD_INTERVALS,
	type PeriodInterval,
	isPeriodInterval,
	isSupportedInterval,
} from "../../types/kpiWorkflow";

interface PeriodRangeFieldsProps {
	periodStart: string;
	periodEnd: string;
	interval: PeriodInterval;
	onPeriodStartChange: (value: string) => void;
	onPeriodEndChange: (value: string) => void;
	onIntervalChange: (value: PeriodInterval) => void;
}

const INPUT_CLASS =
	"w-full rounded-lg border border-gray-300 px-3 py-2 text-base text-text-base focus:border-[#008782] focus:ring-[#008782]";

const PeriodRangeFields: React.FC<PeriodRangeFieldsProps> = ({
	periodStart,
	periodEnd,
	interval,
	onPeriodStartChange,
	onPeriodEndChange,
	onIntervalChange,
}) => (
	<div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
		<div>
			<label
				htmlFor="kpi-period-start"
				className="text-sm text-text-muted block mb-1"
			>
				From
			</label>
			<input
				id="kpi-period-start"
				type="date"
				className={INPUT_CLASS}
				value={periodStart}
				onChange={(event) => onPeriodStartChange(event.target.value)}
			/>
		</div>
		<div>
			<label
				htmlFor="kpi-period-end"
				className="text-sm text-text-muted block mb-1"
			>
				To
			</label>
			<input
				id="kpi-period-end"
				type="date"
				className={INPUT_CLASS}
				value={periodEnd}
				onChange={(event) => onPeriodEndChange(event.target.value)}
			/>
		</div>
		<div>
			<label
				htmlFor="kpi-interval"
				className="text-sm text-text-muted block mb-1"
			>
				Reporting interval
			</label>
			<select
				id="kpi-interval"
				className={INPUT_CLASS}
				value={interval}
				onChange={(event) => {
					const value = event.target.value;
					if (isPeriodInterval(value)) onIntervalChange(value);
				}}
			>
				{PERIOD_INTERVALS.map((option) => (
					// Unsupported intervals stay VISIBLE but disabled. Hiding them
					// would make the roadmap invisible; leaving them selectable would
					// promise extraction the engine does not do.
					<option
						key={option}
						value={option}
						disabled={!isSupportedInterval(option)}
					>
						{INTERVAL_LABELS[option]}
						{isSupportedInterval(option) ? "" : " — coming soon"}
					</option>
				))}
			</select>
			<p className="text-xs text-text-muted mt-1">
				Monthly reporting only for now. Weekly and quarterly are coming.
			</p>
		</div>
	</div>
);

export default PeriodRangeFields;
